import React, { useCallback, useMemo } from 'react';
import styles from './Modal.module.scss';
import { Tabs } from '../tab/Tab';
import { sessionId, socket, URL } from '../socket';
import { Icon } from '../icon/Icon';
import axios from 'axios';

const errorStrings: any = {
    'GAME_DOES_NOT_EXIST': "Game does not exist :("
}

export interface IStartModalProps extends IModalProps {
    players: string[];
    onStartGame: () => void;
    onSelectMultiplayer: () => void;
    onSelectSinglePlayer: () => void;
    onJoinGame: (username: string, roomCode: string) => void;
    countdown: number;
    roomCode?: string;
    error?: string;
    selection?: 'start' | 'waitingroom' | 'singleplayer' | 'multiplayer' | 'countdown';
    onClickBackButton: () => void;
}

export interface IModalProps {
    onClose: () => void;
}

export interface IEndModalProps extends IModalProps {
    time: string;
    winner?: IMultiplayerData;
}

interface IData {
    username: string;
    sessionId?: string | null;
    userId?: string | null;
}

export function StartModal(props: IStartModalProps) {
    const { 
        players, 
        roomCode, 
        onClose, 
        onStartGame, 
        onSelectMultiplayer, 
        error, 
        selection,
        onClickBackButton,
        onJoinGame
    } = props;

    const onClickSinglePlayer = useCallback(() => {
        // Close the modal and start the single player game
        onClose();
    }, [onClose]);

    const onClickMultiplayer = useCallback(() => {
        axios.get(`${URL}/ping`)
        .then(function (response: any) {
          console.log(response);
        }).catch(function (error: any) {
            console.log(error);
        });
        onSelectMultiplayer();
    }, [onSelectMultiplayer]);

    const startContent = useMemo(() => (
        <div className={styles.outerModalContent}>
            <div className={styles.modalContent}>
                <div className={styles.header}>BUDDIES</div>
                <div className={styles.iconContainer}>
                    <Icon iconIndex={0} image={{ img: "fox.png", altText: "fox" }} />
                    <Icon iconIndex={0} image={{ img: "banana.png", altText: "banana" }} />
                    <Icon iconIndex={0} image={{ img: "duck.png", altText: "duck" }} />
                    <Icon iconIndex={0} image={{ img: "tomato.png", altText: "tomato" }} />
                    <Icon iconIndex={0} image={{ img: "dude.png", altText: "dude" }} />
                </div>
                <div className={styles.modalTextContainer}>
                    <div>
                        Welcome to Buddies!
                    </div>
                    <div>
                        Each card has one matching buddy on every other card in the deck.
                    </div>
                    <div>
                        It's your job to find the matching buddies. Good luck!
                    </div>
                </div>
                <div className={styles.modalButtonContainer}>
                    <button onClick={onClickSinglePlayer} className={styles.modalButton}>Single player</button>
                    <button onClick={onClickMultiplayer} className={styles.modalButton}>Multiplayer</button>
                </div>
            </div>
            <div className={styles.aboutMe}>
                Natalie Ethell © 2025
                {/* <a href="https://www.linkedin.com/in/natalieethell/">Hi! :)</a> */}
            </div>
        </div>
    ), [onClickSinglePlayer, onClickMultiplayer]);

    const multiplayerContent = useMemo(() => (
        <MultiplayerContent
            onJoinGame={onJoinGame} 
            onCreateGame={() => {}} 
            error={error}
        />
    ), [error, onJoinGame]);

    return (
        <div className={styles.modalRoot}>
            {
                selection === 'multiplayer' ? (
                    multiplayerContent
                ) : selection === 'singleplayer' ? (
                    <SinglePlayerContent />
                ) : selection === 'waitingroom' ? (
                    <WaitingRoom players={players} roomCode={roomCode} onStartGame={onStartGame} />
                ) : selection === 'countdown' ? (
                    <Countdown countdown={props.countdown} />
                ) : (
                    startContent
                )
            }
            { 
                (selection === 'multiplayer') && 
                <button className={styles.modalButton} onClick={onClickBackButton}>
                    Back
                </button>
            }
        </div>
    );
}

export interface IStartContentProps {
    onClickSinglePlayer: () => void;
    onClickMultiplayer: () => void;
}

export interface IMultiplayerContentProps {
    onJoinGame: (username: string, roomCode: string) => void;
    onCreateGame: (data: IMultiplayerData) => void;
    error?: string;
}

export interface IMultiplayerData {
    roomCode?: string;
    username: string;
    deck?: number[];
}

export function MultiplayerContent(props: IMultiplayerContentProps) {
    const { onCreateGame, error } = props;

    const onClickJoinGame = useCallback((): void => {
        const roomCodeElement = document.getElementById('roomCode') as HTMLInputElement;
        const roomCode = roomCodeElement?.value;
        const nameElement = document.getElementById('name') as HTMLInputElement;
        const username = nameElement?.value;
        if (!roomCode) {
            // setError('Room code cannot be empty');
            return;
        }
        let data: IData = { username: username };
        if (socket.auth && "sessionId" in socket.auth) {
            data.sessionId = sessionId;
        }
        socket.auth = { data };
        socket.connect();
        socket.emit('joinGame', roomCode, username);
    }, []);

    const onClickCreateGame = useCallback((): void => {
        const hostNameElement = document.getElementById('hostName') as HTMLInputElement;
        const hostName = hostNameElement?.value;
        if (!hostName) {
            // TODO: error, hostname cannot be empty
            return;
        }

        let data: IData = { username: hostName };

        if (socket.auth && "sessionId" in socket.auth) {
            data.sessionId = sessionId;
        }
        
        socket.auth = { data };
        socket.connect();
        socket.emit('createGame', hostName);

        onCreateGame(({ username: hostName, deck: [] }));
    }, [onCreateGame]);

    return (
        <div className={styles.modalContent}>
            <Tabs>
                <div className={styles.gameContainer}>
                    <div className={styles.userInfoContainer}>
                        <div>
                            <label htmlFor='roomCode'>Room code:</label>
                            <input title="Room code" id="roomCode" />
                        </div>
                        <div>
                            <label htmlFor='name'>Your name:</label>
                            <input title="Your name" id="name" />
                        </div>
                    </div>
                    <button className={styles.modalButton} onClick={onClickJoinGame}>OK</button>
                </div>
                <div className={styles.gameContainer}>
                    <div>
                        <label htmlFor='hostName'>Your name:</label>
                        <input title="Your name" id="hostName" />
                    </div>
                    <button className={styles.modalButton} onClick={onClickCreateGame}>OK</button>
                </div>
            </Tabs>
            {
                error ? 
                <div>{errorStrings[error] || error}</div> : 
                null
            }
        </div>
    );
}

export interface ISinglePlayerContentProps {
}

export function SinglePlayerContent(props: ISinglePlayerContentProps) {
    return (
        <div className={styles.modalContent}>
            <label htmlFor="fname">Your name:</label>
            <input type="text" id="fname" name="fname" />
        </div>
    );
}

export function StartContent(props: IStartContentProps) {
    const { onClickSinglePlayer, onClickMultiplayer } = props;
    return (
        <div className={styles.modalContent}>
            <div>
                Welcome to Buddies!
            </div>
            <div>
                Each card has one matching buddy on every other card in the deck.
            </div>
            <div>
                It's your job to find all the buddies, good luck!
            </div>
            <button onClick={onClickSinglePlayer} className={styles.modalButton}>Single player</button>
            <button onClick={onClickMultiplayer} className={styles.modalButton}>Multiplayer</button>
        </div>
    );
}

export function EndModal(props: IEndModalProps) {
    const { time, winner } = props;

    return (
        <dialog className={styles.modalRoot} open>
            {
                !winner ? (
                    <div className={styles.modalContent}>
                        You found all the buddies! :-)
                        Sadie says, congratulations!!
                    </div>
                ) : (
                    <div className={styles.modalContent}>
                        Game over!
                        Winner: {winner.username}
                    </div>
                )
            }
            <div>Time: {time}</div>
            <img alt={'sadie'} src={require('../assets/puppy.jpg')} className={styles.modalImg} />
        </dialog>
    );
}

export interface IWaitingRoomProps {
    onStartGame?: () => void;
    players: string[];
    roomCode?: string;
}

export function WaitingRoom(props: IWaitingRoomProps) {
    const { onStartGame, players, roomCode } = props;

    return (
        <dialog className={styles.modalRoot} open>
            <div className={styles.modalContent}>
                Waiting for other players to join...
            </div>
            <div>
                Room code: {roomCode}
            </div>
            <div>
                Current players: {players?.join(', ')}
            </div>
            <button 
                onClick={onStartGame} 
                className={styles.modalButton}
            >
                Start game
            </button>
        </dialog>
    );
}

export interface ICountdownProps {
    countdown: number;
}

export function Countdown(props: ICountdownProps) {
    const { countdown } = props;
    return (
        <div>
            {countdown}
        </div>
    );
}